import { Controller } from '@hotwired/stimulus'

// Connects to data-controller="timezone"
export default class extends Controller {
  static values = {
    format: String,
    timezone: String
  }

  connect() {
    this.convertTimestamp(this.element)
  }

  convertTimestamp(element) {
    const utcTimestamp =
      element.dataset.utcTimestamp || element.textContent.trim()

    if (!utcTimestamp) return

    try {
      const date = new Date(utcTimestamp)

      if (isNaN(date.getTime())) {
        return
      }

      const formattedDate = this.formatDate(date)
      element.textContent = formattedDate

      // Add a title attribute with the original UTC timestamp for reference
      element.title = `UTC: ${utcTimestamp}`
    } catch (error) {
      // Silently fail
    }
  }

  formatDate(date) {
    const format = this.formatValue || 'datetime'
    const timezone = this.timezoneValue || 'auto'

    switch (format) {
      case 'date':
        return this.formatDateOnly(date, timezone)
      case 'time':
        return this.formatTimeOnly(date, timezone)
      case 'relative':
        return this.formatRelativeTime(date)
      case 'datetime':
      default:
        return this.formatDateTime(date, timezone)
    }
  }

  formatDateOnly(date, timezone) {
    if (timezone === 'auto') {
      return date.toLocaleDateString()
    } else {
      return date.toLocaleDateString('en-US', { timeZone: timezone })
    }
  }

  formatTimeOnly(date, timezone) {
    if (timezone === 'auto') {
      return date.toLocaleTimeString()
    } else {
      return date.toLocaleTimeString('en-US', { timeZone: timezone })
    }
  }

  formatDateTime(date, timezone) {
    if (timezone === 'auto') {
      return this.formatFriendlyDateTime(date)
    } else {
      return this.formatFriendlyDateTime(date, timezone)
    }
  }

  formatFriendlyDateTime(date, timezone = null) {
    // Get timezone abbreviation first
    const timezoneAbbr = timezone
      ? this.getTimezoneAbbreviation(date, timezone)
      : this.getTimezoneAbbreviation(date)

    // Format weekday
    const weekdayOptions = { weekday: 'long' }
    if (timezone) weekdayOptions.timeZone = timezone
    const weekday = date.toLocaleDateString('en-US', weekdayOptions)

    // Format date (MM/DD)
    const dateOptions = { month: '2-digit', day: '2-digit' }
    if (timezone) dateOptions.timeZone = timezone
    const dateStr = date.toLocaleDateString('en-US', dateOptions)

    // Format time (HH:MM:SS AM/PM)
    const timeOptions = {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }
    if (timezone) timeOptions.timeZone = timezone
    const timeStr = date.toLocaleTimeString('en-US', timeOptions)

    // Format: "Wednesday, 09/03 3:30:41 PM CST"
    return `${weekday}, ${dateStr} ${timeStr} ${timezoneAbbr}`
  }

  getTimezoneAbbreviation(date, timezone = null) {
    try {
      const options = { timeZoneName: 'short' }
      if (timezone) {
        options.timeZone = timezone
      }

      const parts = date.toLocaleString('en-US', options).split(' ')
      return parts[parts.length - 1] // Get the timezone abbreviation
    } catch (error) {
      return 'UTC'
    }
  }

  formatRelativeTime(date) {
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)

    if (diffInSeconds < 60) {
      return 'just now'
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60)
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600)
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400)
      return `${days} day${days !== 1 ? 's' : ''} ago`
    } else {
      // For older dates, show the actual date
      return date.toLocaleDateString()
    }
  }

  // Method to refresh timestamps (useful for dynamic content)
  refresh() {
    this.convertTimestamps()
  }
}
